<template>
  <div class="post-detail bg-background-light dark:bg-background-dark min-h-screen">
    <AppHeader />
    
    <main class="max-w-[800px] mx-auto px-4 py-10 lg:py-16">
      <!-- 返回按钮 -->
      <button @click="$router.back()" class="flex items-center text-primary mb-8 hover:opacity-80 transition-opacity">
        <span class="material-symbols-outlined mr-1">arrow_back</span> 返回
      </button>

      <div v-if="loading" class="py-20 text-center">
        <el-skeleton :rows="10" animated />
      </div>

      <article v-else-if="post" class="animate__animated animate__fadeIn">
        <header class="mb-10 border-b border-gray-100 dark:border-gray-800 pb-8">
          <h1 class="text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-6">
            {{ post.title }}
          </h1>
          <div class="flex flex-wrap items-center gap-6 text-sm text-gray-500 dark:text-gray-400">
            <div class="flex items-center gap-2">
              <span class="material-symbols-outlined text-base">calendar_today</span>
              {{ formatDate(post.displayTime) }}
            </div>
            <div class="flex items-center gap-2">
              <span class="material-symbols-outlined text-base">visibility</span>
              {{ post.viewCount || 0 }} 次浏览
            </div>
            <div v-if="post.categoryId" class="flex items-center gap-2">
              <span class="material-symbols-outlined text-base">tag</span>
              {{ getCategoryName(post.categoryId) }}
            </div>
          </div>
        </header>

        <!-- 文章正文 -->
        <div class="prose prose-lg dark:prose-invert max-w-none prose-img:rounded-xl prose-headings:text-gray-900 dark:prose-headings:text-white" v-html="post.content">
        </div>

        <div v-if="!post.content" class="py-10 text-center text-gray-400 italic">
          此文章暂无正文内容
        </div>

        <footer class="mt-16 pt-8 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center text-sm text-gray-400">
          <span>本文由湖北省建筑劳务协会发布</span>
          <div class="flex gap-4">
             <button class="hover:text-primary transition-colors">分享</button>
             <button class="hover:text-primary transition-colors" @click="printPage()">打印</button>
          </div>
        </footer>
      </article>

      <div v-else class="py-20 text-center text-gray-400">
        <span class="material-symbols-outlined text-5xl mb-4">error</span>
        <p>未找到该文章或已被删除</p>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from '@/components/AppHeader.vue';
import newsService from '@/api/newsService';
import dayjs from 'dayjs';

const route = useRoute();
const post = ref(null);
const loading = ref(true);

const fetchPost = async () => {
  const id = route.params.id;
  const isNotification = route.path.includes('notification');
  const isPolicy = route.path.includes('policy');
  
  loading.value = true;
  try {
    let res;
    if (isNotification) {
      res = await newsService.getNotification(id);
    } else if (isPolicy) {
      res = await newsService.getPolicyDetail(id);
    } else {
      res = await newsService.getNewsDetail(id);
    }
    
    const data = res.data || res;
    // 数据归一化：处理不同实体的字段差异
    if (data) {
      data.displayTime = data.publishTime || data.createTime;
    }
    post.value = data;
  } catch (error) {
    console.error('获取详情失败:', error);
  } finally {
    loading.value = false;
  }
};

const formatDate = (date) => date ? dayjs(date).format('YYYY年MM月DD日 HH:mm') : '-';

const getCategoryName = (id) => {
  const map = { association: '协会动态', policy: '政策公告', industry: '行业资讯', party: '党建工作' };
  return map[id] || '协会公告';
};

const printPage = () => {
  window.print();
};

onMounted(fetchPost);
</script>

<style scoped>
.prose :deep(img) {
  margin: 2rem auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}
.prose :deep(p) {
  margin-bottom: 1.5rem;
  line-height: 1.8;
  color: #374151;
}
.dark .prose :deep(p) {
  color: #d1d5db;
}
</style>
